import { useMemo } from 'react';
import moment from 'moment';
import i18n from 'i18n-js';
import { GaugeRowProps } from '@components/wallboard/types';
import { ncFileStatuses } from '@components/wallboard/components/functions';

export interface UseManageGaugeRowParams extends Pick<GaugeRowProps, 'batch' | 'oeeValue' | 'firstScheduledJob' | 'ncFileStatus'> {}

const useManageGaugeRow = ({ batch, oeeValue, firstScheduledJob, ncFileStatus }: UseManageGaugeRowParams) => {
  // --- Pieces data ---
  const quantityReady = batch?.quantityReady ?? 0;
  const quantityTarget = useMemo(() => {
    const total = batch?.links?.reduce((acc: number, l: any) => acc + l.quantityTarget, 0);
    return total === 0 ? null : total ?? null;
  }, [batch?.links]);

  const hasTarget = quantityTarget != null && quantityTarget > 0;
  const displayReady = Math.min(quantityReady, 99999);
  const displayTarget = hasTarget ? Math.min(quantityTarget!, 99999) : null;
  const maxDigits = Math.max(String(displayReady).length, displayTarget != null ? String(displayTarget).length : 0);
  const showFraction = displayTarget != null && maxDigits <= 3;

  // --- OEE data ---
  const hasOEE = oeeValue != null;
  const oeeRounded = hasOEE ? Math.round(oeeValue!) : 0;

  // --- Schedule data ---
  const actualStartDate = firstScheduledJob?.productionStartedOn || firstScheduledJob?.actualStartDate || null;
  const scheduledStartDate = firstScheduledJob?.scheduledStartDate ?? null;

  const { daysDiff, isAhead, isBehind, hasScheduleData } = useMemo(() => {
    if (!actualStartDate || !scheduledStartDate) {
      return { daysDiff: 0, isAhead: false, isBehind: false, hasScheduleData: false };
    }
    const actual = moment(actualStartDate);
    const scheduled = moment(scheduledStartDate);
    const diffInDays = Math.abs(Math.floor(actual.diff(scheduled, 'seconds') / 86400));
    return {
      daysDiff: Math.min(diffInDays, 999),
      isAhead: scheduled.isAfter(actual),
      isBehind: actual.isAfter(scheduled),
      hasScheduleData: true,
    };
  }, [actualStartDate, scheduledStartDate]);

  const scheduleNotLinkedText =
    ncFileStatus === ncFileStatuses.Unknown ? i18n.t('Unknown NC File Type') : i18n.t('Batch not linked to Job');

  return {
    quantityReady,
    quantityTarget,
    hasTarget,
    displayReady,
    displayTarget,
    showFraction,
    hasOEE,
    oeeRounded,
    daysDiff,
    isAhead,
    isBehind,
    hasScheduleData,
    scheduleNotLinkedText,
  };
};

export default useManageGaugeRow;
